import { type FC, type PropsWithChildren } from 'react';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

type Props = PropsWithChildren;

const Title: FC<Props> = ({ children }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <div className={clsx(
      // `sticky` en vez de `fixed`: así el título respeta el ancho de su
      // contenedor (la columna de contenido, ya desplazada por el ancho
      // real del sidebar) en lugar de estirarse a todo el viewport y
      // quedar por encima del sidebar. Al seguir en el flujo normal,
      // tampoco hacen falta márgenes de compensación en el componente
      // padre (Screen) para dejarle hueco.
      'flex items-center sticky w-full z-10',
      'px-0 py-5 md:px-5 md:py-5',
      'transition-all duration-500',
      // Mobile: below mobile header (64px), Desktop: top-0
      'top-16 md:top-0',
      // Light mode background
      'bg-white/95 backdrop-blur-sm border-b border-gray-200',
      // Dark mode background
      'dark:bg-background/95 dark:border-borders',
      // Dark container variant
      isDarkMode && 'bg-transparent dark:bg-transparent border-b-0'
    )}>
      {/* Small decorator */}
      <div className={clsx(
        'h-1 w-8 md:w-8 rounded-full transition-all duration-500',
        'bg-gradient-to-r from-blue-500 to-purple-500',
        'dark:from-blue-400 dark:to-purple-400',
        'ml-4 mr-2.5 md:mx-2.5',
        'shadow-sm'
      )} />

      {/* Title */}
      <h1 className={clsx(
        'font-light text-2xl md:text-4xl uppercase tracking-wide transition-colors duration-500',
        'text-gray-900 dark:text-white',
        'md:font-light font-normal'
      )}>
        {children}
      </h1>

      {/* Long decorator */}
      <div className={clsx(
        'h-1 flex-1 rounded-full transition-all duration-500',
        'bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500',
        'dark:from-purple-400 dark:via-pink-400 dark:to-blue-400',
        'mr-4 ml-2.5 md:mx-4',
        'shadow-sm',
        'relative overflow-hidden',
        // Subtle shimmer effect
        'before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-1000'
      )} />
    </div>
  );
};

export { Title };
